import { zodResolver } from '@hookform/resolvers/zod';

import { useForm } from 'react-hook-form';

import {
  Or2Dialog,
  type Or2DialogProps,
} from '@/components/feedback/Or2Dialog';
import type { DocumentType } from '@/gql';
import { graphql } from '@/gql';

import { Or2Button } from '@/components/button/Or2Button';
import { useMutation } from '@/hooks/mutationWithSnackbar';

import type { UrlFileFormData } from '../forms/UrlFileForm';
import { UrlFileForm, UrlFileFormSchema } from '../forms/UrlFileForm';

export const FILE_URL_DIALOG_FILE_FRAGMENT = graphql(`
  fragment fileUrlDialogFileFields on File {
    _id
    filename
    url
    parent {
      ...urlFileFormParentFields
    }
  }
`);

export const FILE_URL_DIALOG_FILES_FOR_OWNER_FRAGMENT = graphql(`
  fragment fileUrlDialogFilesForOwnerFields on FilesForOwner {
    owner {
      ... on Group {
        _id
        name
        machineName
      }
      ... on Service {
        _id
        name
        machineName
      }
    }
  }
`);

const UPDATE_FILE_URL_MUTATION = graphql(`
  mutation UpdateFileUrl($data: UpdateUrlFileInput) {
    updateUrlFile(params: $data) {
      file {
        ...fileFields
      }
    }
  }
`);

interface Props extends Omit<Or2DialogProps, 'title'> {
  file: DocumentType<typeof FILE_URL_DIALOG_FILE_FRAGMENT>;
  filesForOwner: DocumentType<typeof FILE_URL_DIALOG_FILES_FOR_OWNER_FRAGMENT>;
}

export const UpdateFileUrlDialog = ({
  file,
  filesForOwner,
  onClose,
  ...other
}: Props) => {
  const { control, handleSubmit, setValue } = useForm<UrlFileFormData>({
    defaultValues: {
      filename: file.filename ?? '',
      url: file.url ?? '',
    },
    resolver: zodResolver(UrlFileFormSchema),
  });

  const [updateFileUrl, { loading }] = useMutation(UPDATE_FILE_URL_MUTATION, {
    completionMessage: 'Link updated',
    onCompleted: onClose,
  });

  const onSubmit = (data: UrlFileFormData) => {
    updateFileUrl({
      variables: {
        data: {
          file: file._id,
          filename: data.filename,
          url: data.url,
        },
      },
    });
  };

  return (
    <Or2Dialog
      maxWidth="xs"
      loading={loading}
      onClose={onClose}
      title="Update a web link"
      actions={
        <>
          <Or2Button color="secondary" onClick={onClose}>
            Cancel
          </Or2Button>
          <Or2Button
            onClick={handleSubmit(onSubmit)}
            color="primary"
            disabled={loading}
          >
            Update
          </Or2Button>
        </>
      }
      {...other}
    >
      <UrlFileForm
        parent={file.parent}
        filesForOwner={filesForOwner}
        control={control}
        setValue={setValue}
      />
    </Or2Dialog>
  );
};
